import { createInertiaApp } from "@inertiajs/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

// Declared once at module scope rather than inside resolve(). Vite hoists the
// glob either way, but this makes it obvious it is not per-call work.
const pages = import.meta.glob("../pages/**/*.jsx");

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  progress: {
    delay: 250,
    color: "#D85907",
    includeCSS: true,
    showSpinner: false,
  },

  resolve: async (name) => {
    // Lazy on purpose. With `{ eager: true }` Vite turns this into a static
    // import of every file under pages/, so the entry chunk carried all 34 page
    // components and their dependency graphs -- communities, exams, portal,
    // sertifikasi, webinars -- on every page load, including `/`. That built to
    // a single 1,426 kB chunk and Vite's own size warning fired on it.
    // Returning the import thunk instead gives Rollup a split point per page.
    const importPage = pages[`../pages/${name}.jsx`];
    if (!importPage) {
      console.error(`Missing Inertia page component: '${name}.jsx'`);
      return undefined;
    }

    const page = await importPage();

    // In development mode, you can attach the name to the component object
    if (import.meta.env.DEV) {
      page.default.displayName = `Inertia_${name.replace(/\//g, "_")}`;
    }

    // To use a default layout, import the Layout component
    // and use the following lines.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page) => (<Layout>{page}</Layout>)

    return page;
  },

  setup({ el, App, props }) {
    // You can also add the component name as data attribute to the root element
    if (import.meta.env.DEV) {
      el.setAttribute("data-inertia-component", props.initialPage.component);
    }
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    );
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    future: {
      useScriptElementForInitialPage: false,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error;
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_javascript_tag "inertia.jsx" %> to the Inertia-specific layout instead.',
    );
  }
});
